import { useEffect } from 'react';
import { environmentLabel, envName } from './environment.js';

/**
 * A standing mark that this is not production.
 *
 * Rendered only when the build says it is not production (`environment.ts`), so
 * this component never has to ask -- mounting it is the decision.
 *
 * Outside `<main>` and full width, because the one job is to be impossible to
 * mistake for part of the app. Inside the measure it would read as a message
 * about the catalog rather than about the deployment, which is the confusion in
 * miniature.
 *
 * Not a live region and not an alert. It says the same thing on every screen
 * for as long as the session lasts, and `role="alert"` would interrupt a screen
 * reader with it on every navigation. In document order before the content, it
 * is simply read first, which is when it is useful.
 */
export function EnvironmentBanner() {
  const name = envName();

  /*
   * The tab title too.
   *
   * The banner is at the top of the page, so it stops being visible the moment
   * anyone scrolls -- and the moment that matters is when they act on something,
   * which is anywhere. The title is the one surface that is always on screen,
   * and it is what tells two tabs apart when one is dev and one is not.
   */
  useEffect(() => {
    const original = document.title;
    document.title = `[${name}] ${original}`;
    return () => {
      document.title = original;
    };
  }, [name]);

  /*
   * A landmark, because content outside every landmark cannot be reached by
   * someone navigating with them -- the audit caught this sitting orphaned
   * beside `main`, which would have made the one piece of standing context on
   * the page the one piece a screen reader user had to hunt for.
   *
   * `aside` and not `header`. `header` here would claim the page's `banner`
   * landmark, which belongs to site-wide navigation the app may yet grow; this
   * is supporting information about the deployment. Labelled, so it is
   * distinguishable in a list of landmarks rather than reading as "complementary".
   */
  return (
    <aside className="env-banner" aria-label="Environment">
      <strong>{environmentLabel(name)} environment.</strong> This is not your live catalog.
    </aside>
  );
}
